import React, {
  lazy,
  Suspense,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import * as s from './styled-machines';
import {
  EmptyState,
  Icon,
  ModalClose,
  ModalConfirmation,
} from '../../components';
import {
  notifyError,
  notifySuccess,
  sessionCache,
  useDebounce,
} from '../../utils';
import { icons } from '../../assets';
import {
  getCustomers,
  postNewMaquina,
  postEditMaquina,
  getMaquinaFilters,
  getMaquina,
  toggleMachineActive,
} from '../../services';

// Lazy-load do modal de cadastro/edicao — bundle pesado (Formik + yup
// + VariableLinker + AsyncSelect). So carrega quando o operador abre.
const CadastroMaquinaModal = lazy(() =>
  import('../../components/machine-form/machine-form'),
);

// Inline icons (lucide-style) — chrome da tela. Acoes de linha
// continuam usando <Icon> (pencil) pra consistencia com outras telas.
const ISearch = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

const IPlus = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </svg>
);

const normalizeOptionLabel = (option) =>
  String(option?.type || option?.label || '')
    .trim()
    .toLowerCase();

const isBlueboxOption = (option) => normalizeOptionLabel(option) === 'bluebox';

const isApplicationTypeOption = (option) =>
  ['ucha', 'eba'].includes(normalizeOptionLabel(option));

const buildThingTypeOptions = (thingTypeOptions, applianceTypeOptions) => {
  const hasBlueboxThingType = thingTypeOptions.some(isBlueboxOption);
  const blueboxFromApplicationType = applianceTypeOptions.find(isBlueboxOption);

  if (hasBlueboxThingType || !blueboxFromApplicationType) {
    return thingTypeOptions;
  }

  return [
    ...thingTypeOptions,
    {
      ...blueboxFromApplicationType,
      label: 'bluebox',
    },
  ];
};

const RelacaoMaquinas = () => {
  // --- role (controle de acesso ativar/desativar) ---
  // So higra/admin enxerga o toggle por linha + "Mostrar desativadas".
  // Operadores comuns nao veem maquinas inativas e nao podem alterar
  // status — backend tambem aplica guard, mas escondemos UI por UX.
  const { user } = useSelector((state) => state.usuario);
  const role = user && user.role;
  const canManageActive = role === 'higra' || role === 'admin';

  // --- estado de listagem ---
  const [currentData, setCurrentData] = useState([]);
  const [amount, setAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [itensPerPage, setItensPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [filterText, setFilterText] = useState('');
  // debouncedFilterText so atualiza 350ms apos o usuario parar de digitar
  // — evita disparar fetch a cada keystroke.
  const debouncedFilterText = useDebounce(filterText, 350);
  const [currentEmpresa, setCurrentEmpresa] = useState('');
  const [currentEstacao, setCurrentEstacao] = useState('');
  // Mostrar desativadas — admin-only. Inclui no fetch (includeInactive=true)
  // pra trazer rows com active:false do backend (que por default filtra).
  const [showInactive, setShowInactive] = useState(false);
  // Linha alvo do toggle ativar/desativar — controla o ModalConfirmation.
  // null = nao ha confirmacao pendente.
  const [toggleTarget, setToggleTarget] = useState(null);
  const [togglingActive, setTogglingActive] = useState(false);

  // --- dropdowns ---
  const [listCurrentCustomers, setListCurrentCustomers] = useState([]);
  const [listCurrentEstacao, setListCurrentEstacao] = useState([]);
  // Lista crua sem item "Selecione" pra repassar ao modal de cadastro.
  const [listEstacao, setListEstacao] = useState([]);
  const [listType, setListType] = useState([]);
  const [listThingType, setThingType] = useState([]);
  const [blueboxSuggestedVariables, setBlueboxSuggestedVariables] = useState([]);

  // --- modais ---
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [closeConfirmation, setCloseConfirmation] = useState(false);
  const [creatingMaquina, setCreatingMaquina] = useState(false);
  const [telaConcluido, setTelaConcluido] = useState(false);
  // Edit flow: mantemos toda a logica que antes morava no TableMaquinas.
  const [isModalEditVisible, setIsModalEditVisible] = useState(false);
  const [isModalEditVisibleConcluido, setIsModalEditVisibleConcluido] =
    useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [estacaoEditado, setEstacaoEditado] = useState();
  const [maquinaSelecionada, setMaquinaSelecionada] = useState({
    name: '',
    appliance: '',
    estacao: '',
    tipoMaquina: '',
    tipo: '',
    modelo: '',
    serialNumber: '',
    voltagem: '',
    efeciencia: '',
    enabledSetpointThing: [],
    enabledVariableThing: [],
  });

  const formikInitialValuesMaquina = {
    name: '',
    appliance: '',
    estacao: '',
    tipoMaquina: '',
    tipo: '',
    modelo: '',
    serialNumber: '',
    voltagem: '',
  };

  // Token de fetch — incrementado a cada nova busca. Quando uma response
  // chega, comparamos com o token atual: se for diferente, descarta.
  // Evita race quando o operador troca filtros rapido (response antiga
  // chega depois da nova e sobrescreve dados corretos).
  const fetchTokenRef = useRef(0);

  // ============================================================
  // Fetch principal — server-side com paginacao + filtros
  // ============================================================
  const buscaMaquinas = useCallback(() => {
    fetchTokenRef.current += 1;
    const myToken = fetchTokenRef.current;
    setLoading(true);
    // includeInactive: backend filtra active:false por default, so
    // mandamos a flag quando o operador (higra/admin) liga o toggle.
    getMaquina(
      currentPage + 1,
      itensPerPage,
      debouncedFilterText,
      currentEstacao,
      currentEmpresa,
      canManageActive && showInactive,
    )
      .then(({ data }) => {
        if (myToken !== fetchTokenRef.current) return;
        setCurrentData(data[0] || []);
        setAmount(data[1] || 0);
      })
      .catch((error) => {
        if (myToken !== fetchTokenRef.current) return;
        if (error && error.message) notifyError(error.message);
        else notifyError('Ocorreu um erro ao carregar as máquinas');
      })
      .finally(() => {
        if (myToken === fetchTokenRef.current) setLoading(false);
      });
  }, [
    currentPage,
    itensPerPage,
    debouncedFilterText,
    currentEstacao,
    currentEmpresa,
    canManageActive,
    showInactive,
  ]);

  useEffect(() => {
    buscaMaquinas();
    // Cleanup: bump token pra ignorar response em-voo quando o componente
    // desmontar (evita setState em componente desmontado).
    return () => {
      fetchTokenRef.current += 1;
    };
  }, [buscaMaquinas, telaConcluido]);

  // Reseta pra pagina 1 quando filtros/itensPerPage mudam.
  useEffect(() => {
    setCurrentPage(0);
  }, [
    itensPerPage,
    currentEstacao,
    currentEmpresa,
    debouncedFilterText,
    showInactive,
  ]);

  // ============================================================
  // Dropdown options — sessionCache em filters + customers
  // ============================================================
  const buscaMaquinaFilters = useCallback(() => {
    // sessionCache: parks + applianceTypes + thingTypes raramente mudam
    // por sessao. Cache global compartilhado entre telas.
    sessionCache('relacaoMaquinasFilters', 'all', () => getMaquinaFilters())
      .then(({ data }) => {
        // Copia antes de ordenar — sort() muta in-place e como `data`
        // vem do sessionCache, leituras subsequentes ja viriam ordenadas
        // (violando contrato de imutabilidade do cache).
        const sortedParks = [...data.park[0]].sort((a, b) =>
          a.name.localeCompare(b.name),
        );

        const listPark = sortedParks.map((park) => ({
          label: park.name,
          value: park.id,
        }));
        listPark.unshift({ label: 'Selecione', value: '' });
        setListCurrentEstacao(listPark);

        const applianceTypeOptions = data.applianceType[0].map(
          (applianceType) => ({
            label: applianceType.type.toUpperCase(),
            value: applianceType.id,
            type: applianceType.type,
          }),
        );
        const thingTypeOptions = data.thingType[0].map((thingType) => ({
          label: thingType.type,
          value: thingType.id,
          type: thingType.type,
        }));

        setListType(applianceTypeOptions.filter(isApplicationTypeOption));
        setThingType(
          buildThingTypeOptions(thingTypeOptions, applianceTypeOptions),
        );
        setListEstacao(
          sortedParks.map((park) => ({
            label: park.name,
            value: park.id,
          })),
        );
        setBlueboxSuggestedVariables(data.blueboxSuggestedVariables || []);
      })
      .catch((error) => {
        if (error && error.message) notifyError(error.message);
        else notifyError('Ocorreu um erro ao carregar os filtros');
      });
  }, []);

  const buscaCustomers = useCallback(() => {
    // sessionCache: lista completa de clientes do tenant — muda
    // raramente. Cache compartilhado com /stations e /customers.
    sessionCache('allCustomers', 'all', () => getCustomers('', '', '', '', ''))
      .then(({ data }) => {
        const filterCustomers = [{ label: 'Selecione', value: '' }];
        (data[0] || []).forEach((customer) => {
          filterCustomers.push({ label: customer.name, value: customer.id });
        });
        setListCurrentCustomers(filterCustomers);
      })
      .catch((error) => {
        if (error && error.message) notifyError(error.message);
        else notifyError('Ocorreu um erro ao carregar os clientes');
      });
  }, []);

  useEffect(() => {
    buscaCustomers();
    buscaMaquinaFilters();
  }, [buscaCustomers, buscaMaquinaFilters]);

  // ============================================================
  // Cadastro / Edicao
  // ============================================================
  const cadastrarMaquinaNovo = (values) => {
    setCreatingMaquina(true);
    let body = {
      typeId: parseInt(values.tipo, 10),
      parkId: parseInt(values.estacao, 10),
      statusTypeId: 5,
      thing: values.name,
      model: values.modelo,
      serialNumber: values.serialNumber || '',
      thingType: parseInt(values.tipoMaquina, 10),
      enabledSetpoints: values.enabledSetpoints,
      enabledVariables: values.enabledVariables,
    };
    // Cadastrar maquina em aplicacao existente
    if (values.applianceId) {
      body.applianceId = parseInt(values.applianceId, 10);
    } else {
      // Cadastrar maquina em nova aplicacao
      body = {
        ...body,
        v: values.voltagem,
        description: values.appliance,
      };
    }
    postNewMaquina(body)
      .then(() => setTelaConcluido(true))
      .catch((error) => {
        if (error && error.message) notifyError(error.message);
        else notifyError('Ocorreu um erro ao salvar a estação');
      })
      .finally(() => setCreatingMaquina(false));
  };

  // Edit submit — antes vivia em TableMaquinas. Recebe `values` do
  // CadastroMaquinaModal e dispara PATCH no backend; ao concluir
  // refaz busca pra refletir mudancas.
  const editarMaquina = (values) => {
    postEditMaquina(maquinaSelecionada.applianceId, {
      model: values.modelo,
      v: values.voltagem,
      type: parseInt(values.tipo, 10),
      park: parseInt(values.estacao, 10),
      description: values.appliance,
      serialNumber: values.serialNumber || '',
      thingId: parseInt(maquinaSelecionada.id, 10),
      enabledSetpoints: values.enabledSetpoints,
      enabledVariables: values.enabledVariables,
    })
      .then(() => {
        setIsModalEditVisible(false);
        setConfirmationModal(false);
        setIsModalEditVisibleConcluido(true);
        notifySuccess('Máquina editada com sucesso!');
        buscaMaquinas();
      })
      .catch((error) => {
        if (error && error.message) notifyError(error.message);
        else notifyError('Ocorreu um erro ao salvar a máquina');
        setConfirmationModal(false);
      });
  };

  // ============================================================
  // Ativar / desativar maquina (soft hide)
  // ============================================================
  // active=false oculta a thing em listagens e variaveis vinculadas
  // (dados preservados). Reativar mostra de novo. Backend tambem
  // guarda role higra/admin, mas escondemos o controle por UX.
  const confirmarToggleAtivo = () => {
    if (!toggleTarget) return;
    const row = toggleTarget;
    const isActive = row.active !== false;
    const novaActive = !isActive;
    setTogglingActive(true);
    toggleMachineActive(row.id, novaActive)
      .then(() => {
        notifySuccess(
          novaActive
            ? 'Máquina reativada com sucesso!'
            : 'Máquina desativada e oculta do sistema.',
        );
        setToggleTarget(null);
        buscaMaquinas();
      })
      .catch((error) => {
        if (error && error.message) notifyError(error.message);
        else notifyError('Não foi possível atualizar o status da máquina.');
      })
      .finally(() => setTogglingActive(false));
  };

  // Abre modal de edicao populando formik com a linha.
  const abrirEdicao = (row) => {
    setMaquinaSelecionada({
      id: row.id ? row.id : '',
      name: row.thing ? row.thing : '',
      appliance: row.appliance ? row.appliance.description : '',
      applianceId: row.appliance ? row.appliance.id : '',
      estacao: row.park ? row.park.id : '',
      tipoMaquina: row.type ? row.type.id : '',
      tipo: row.appliance ? row.appliance.type.id : '',
      modelo: row.model ? row.model : '',
      serialNumber: row.serialNumber || row.serial_number || '',
      voltagem: row.appliance ? row.appliance.v : '',
      enabledSetpointThing: row?.enabledSetpointThing ?? [],
      enabledVariableThing: row?.enabledVariableThing ?? [],
    });
    setIsModalEditVisible(true);
  };

  // ============================================================
  // Paginacao com elipses (1, 2, 3, …, n-2, n-1, n)
  // ============================================================
  const pageCount = Math.ceil(amount / itensPerPage) || 0;

  const pagList = useMemo(() => {
    const list = [];
    const total = pageCount;
    const pg = currentPage + 1;
    if (total <= 7) {
      for (let i = 1; i <= total; i += 1) list.push(i);
    } else {
      list.push(1, 2, 3);
      if (pg > 5) list.push('…');
      if (pg > 3 && pg < total - 2) list.push(pg);
      if (pg < total - 4) list.push('…');
      list.push(total - 2, total - 1, total);
    }
    return Array.from(new Set(list));
  }, [pageCount, currentPage]);

  // Helpers pra extrair labels da linha bruta do backend.
  const rowMaquina = (row) => row.thing || '—';
  const rowSerial = (row) => row.serialNumber || row.serial_number || '';
  const rowCliente = (row) =>
    (row.park && row.park.customer && row.park.customer.name) || '—';
  const rowEstacao = (row) => (row.park && row.park.name) || '—';
  // Ultima atualizacao de dados (thing.lastupdate). Aceita ISO string ou
  // epoch (s/ms) e formata "DD/MM/AAAA - HH:MM". Fallback "—" quando
  // ausente (ex.: backend ainda nao retorna o campo, ou thing sem leitura).
  const rowAtualizacao = (row) => {
    const raw = row.lastupdate;
    if (!raw && raw !== 0) return '—';
    let date = new Date(raw);
    if (Number.isNaN(date.getTime())) {
      const n = Number(raw);
      if (!Number.isFinite(n)) return '—';
      date = new Date(n < 1e12 ? n * 1000 : n);
      if (Number.isNaN(date.getTime())) return '—';
    }
    const d = date.toLocaleDateString('pt-BR');
    const h = date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
    return `${d} - ${h}`;
  };

  return (
    <s.Container className="fade-in">
      {/* Page Header — titulo + sub + contador */}
      <s.PageHeader>
        <s.PageHeaderText>
          <s.PageTitle>Máquinas</s.PageTitle>
          <s.PageSub>
            Cadastre máquinas vinculadas a clientes, estações e variáveis
            monitoradas.
          </s.PageSub>
        </s.PageHeaderText>
        <s.PageCount>
          <b>{amount}</b> {amount === 1 ? 'máquina' : 'máquinas'}
        </s.PageCount>
      </s.PageHeader>

      {/* Filters — busca | cliente | estacao | CTA */}
      <s.Filters>
        <s.Search>
          {ISearch}
          <input
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            placeholder="Pesquise por máquina, cliente ou estação"
          />
        </s.Search>

        <s.SelCell>
          <select
            value={currentEmpresa}
            onChange={(e) => setCurrentEmpresa(e.target.value)}
          >
            <option value="">Cliente</option>
            {listCurrentCustomers
              .filter((o) => o.value !== '')
              .slice()
              .sort((a, b) => (a.label || '').localeCompare(b.label || '', 'pt-BR', { sensitivity: 'base' }))
              .map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
          </select>
        </s.SelCell>

        <s.SelCell>
          <select
            value={currentEstacao}
            onChange={(e) => setCurrentEstacao(e.target.value)}
          >
            <option value="">Estação</option>
            {listCurrentEstacao
              .filter((o) => o.value !== '')
              .slice()
              .sort((a, b) => (a.label || '').localeCompare(b.label || '', 'pt-BR', { sensitivity: 'base' }))
              .map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
          </select>
        </s.SelCell>

        <s.Cta type="button" onClick={() => setIsModalVisible(true)}>
          <span className="ic">{IPlus}</span>
          <span className="tx">
            <span className="t">Cadastro</span>
            <span className="s">Inserir máquina</span>
          </span>
        </s.Cta>
      </s.Filters>

      {/* AdminBar — toggle "Mostrar desativadas" (higra/admin) */}
      {canManageActive && (
        <s.AdminBar>
          <s.ShowInactiveToggle>
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Mostrar máquinas desativadas
          </s.ShowInactiveToggle>
        </s.AdminBar>
      )}

      {/* Table — grid varia: 4-col padrao OU 6-col p/ higra/admin (com
         colunas extras Status + Ativacao). */}
      <s.TableWrap>
        <s.Table $admin={canManageActive}>
          <s.Th>Editar</s.Th>
          <s.Th>Máquina</s.Th>
          <s.Th>Série</s.Th>
          <s.Th>Cliente</s.Th>
          <s.Th>Estação</s.Th>
          <s.Th>Atualização</s.Th>
          {canManageActive && <s.Th>Status</s.Th>}
          {canManageActive && <s.Th>Ativação</s.Th>}

          {loading
            ? Array.from(
                { length: itensPerPage },
                (_, i) => `sk-${itensPerPage}-${i}`,
              ).map((key) => (
                <s.Tr key={key}>
                  <s.SkeletonCell $w="40%" />
                  <s.SkeletonCell $w="70%" />
                  <s.SkeletonCell $w="45%" />
                  <s.SkeletonCell $w="55%" />
                  <s.SkeletonCell $w="65%" />
                  <s.SkeletonCell $w="80%" />
                  {canManageActive && <s.SkeletonCell $w="50%" />}
                  {canManageActive && <s.SkeletonCell $w="60%" />}
                </s.Tr>
              ))
            : currentData.length
            ? currentData.map((row) => {
                // active e opcional no backend — rows legacy sem o campo
                // continuam tratadas como ativas. Apenas active===false
                // explicito ativa visual "desativada".
                const isActive = row.active !== false;
                // Wrapper: InactiveRow aplica fade nas celulas de dados;
                // Tr normal pra rows ativas. Toggle + Status ficam sempre
                // em opacidade cheia.
                const RowWrapper = isActive ? s.Tr : s.InactiveRow;
                return (
                  <RowWrapper key={row.id}>
                    <s.Tc data-label="Editar">
                      <s.RowActions>
                        <s.IconBtn
                          type="button"
                          title="Editar"
                          aria-label={`Editar máquina ${row.name || ''}`}
                          onClick={() => abrirEdicao(row)}
                        >
                          <Icon name="pencil" size={16} />
                        </s.IconBtn>
                      </s.RowActions>
                    </s.Tc>
                    <s.TcMaquina>{rowMaquina(row)}</s.TcMaquina>
                    <s.TcSerial>{rowSerial(row)}</s.TcSerial>
                    <s.TcCliente>{rowCliente(row)}</s.TcCliente>
                    <s.TcEstacao>{rowEstacao(row)}</s.TcEstacao>
                    <s.TcAtualizacao>{rowAtualizacao(row)}</s.TcAtualizacao>
                    {canManageActive && (
                      <s.Tc data-label="Status">
                        <s.StatusCell $active={isActive}>
                          {isActive ? 'Ativo' : 'Inativo'}
                        </s.StatusCell>
                      </s.Tc>
                    )}
                    {canManageActive && (
                      <s.Tc data-label="Ativação">
                        <s.ToggleSwitch
                          title={
                            isActive
                              ? 'Desativar máquina'
                              : 'Reativar máquina'
                          }
                        >
                          <input
                            type="checkbox"
                            checked={isActive}
                            disabled={togglingActive}
                            onChange={() => setToggleTarget(row)}
                          />
                          <span className="slider" />
                        </s.ToggleSwitch>
                      </s.Tc>
                    )}
                  </RowWrapper>
                );
              })
            : (
                <s.EmptyRow>
                  <EmptyState
                    title="Nenhuma máquina encontrada"
                    description="Ajuste a busca ou os filtros e tente novamente."
                    actionLabel="Limpar filtros"
                    onAction={() => {
                      setFilterText('');
                      setCurrentEmpresa('');
                      setCurrentEstacao('');
                    }}
                  />
                </s.EmptyRow>
              )}
        </s.Table>
      </s.TableWrap>

      {/* Pager — Anterior / 1 2 3 ... n / Proximo + exibir N */}
      <s.Pager>
        <button
          className="btn"
          type="button"
          onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
          disabled={currentPage === 0 || pageCount === 0}
        >
          Anterior
        </button>
        <div className="pages">
          {pagList.map((p, idx) =>
            p === '…' ? (
              // eslint-disable-next-line react/no-array-index-key
              <span key={`dots-${idx}`} className="dots">
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                className={`pg ${currentPage + 1 === p ? 'on' : ''}`}
                onClick={() => setCurrentPage(p - 1)}
              >
                {p}
              </button>
            ),
          )}
        </div>
        <button
          className="btn"
          type="button"
          onClick={() =>
            setCurrentPage((p) => Math.min(pageCount - 1, p + 1))
          }
          disabled={currentPage >= pageCount - 1 || pageCount === 0}
        >
          Próximo
        </button>
        <div className="exhibit">
          exibir
          <select
            value={itensPerPage}
            onChange={(e) => setItensPerPage(Number(e.target.value))}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>
      </s.Pager>

      {/* ============== Modais (preservados, com toda logica) ============== */}

      {/* Cadastro */}
      {isModalVisible && (
        <Suspense fallback={null}>
        <CadastroMaquinaModal
          onClose={() => {
            setIsModalVisible(false);
            setTelaConcluido(false);
          }}
          onCloseConfirmation={setCloseConfirmation}
          submit={cadastrarMaquinaNovo}
          estacaoConcluido={telaConcluido}
          setEstacaoConcluido={setTelaConcluido}
          setConfirmationModal={() => {}}
          formikInitialValues={formikInitialValuesMaquina}
          title="Cadastro de Máquina"
          dadosAbaixo="Preencha os dados abaixo:"
          msgButton="Ao cadastrar, um convite será enviado por e-mail"
          labelButton="CADASTRAR"
          sucesso="Máquina cadastrada com sucesso!"
          iconFinal={icons.EstacoesIconFinish}
          listSelectsLists={{
            listEstacao,
            listType,
            listThingType,
            blueboxSuggestedVariables,
          }}
          loading={creatingMaquina}
        />
        </Suspense>
      )}

      {/* Edicao */}
      {isModalEditVisible && (
        <Suspense fallback={null}>
        <CadastroMaquinaModal
          onClose={() => setIsModalEditVisible(false)}
          onCloseConfirmation={setCloseConfirmation}
          submit={setEstacaoEditado}
          setConfirmationModal={setConfirmationModal}
          setEstacaoConcluido={setIsModalEditVisibleConcluido}
          estacaoConcluido={isModalEditVisibleConcluido}
          formikInitialValues={maquinaSelecionada}
          listSelectsLists={{
            listEstacao,
            listType,
            listThingType,
            blueboxSuggestedVariables,
          }}
          title="Editar máquina"
          dadosAbaixo="Modifique os dados abaixo:"
          msgButton="Confirme suas modificações"
          labelButton="SALVAR"
          sucesso="Máquina editada com sucesso!"
          estadoValue={maquinaSelecionada.estado}
          iconFinal={icons.EstacoesIconEdit}
          isEdicao
        />
        </Suspense>
      )}

      {/* Sucesso pos-edicao */}
      {isModalEditVisibleConcluido && (
        <Suspense fallback={null}>
        <CadastroMaquinaModal
          onClose={() => setIsModalEditVisibleConcluido(false)}
          onCloseConfirmation={setCloseConfirmation}
          setEstacaoConcluido={setIsModalEditVisibleConcluido}
          formikInitialValues={maquinaSelecionada}
          listSelectsLists={{
            listEstacao,
            listType,
            listThingType,
            blueboxSuggestedVariables,
          }}
          estacaoConcluido={isModalEditVisibleConcluido}
          title="Editar máquina"
          sucesso="Estação editada com sucesso!"
          iconFinal={icons.EstacoesIconEdit}
          isEdicao
        />
        </Suspense>
      )}

      {/* Confirmacao de edicao — dispara o PATCH */}
      {confirmationModal && (
        <ModalConfirmation
          onClose={() => editarMaquina(estacaoEditado)}
          onCloseConfirmation={setConfirmationModal}
        />
      )}

      {/* Confirmacao de ativar/desativar — modal dedicado pra evitar
         clique acidental que oculta maquina (ou reativa indevidamente)
         em todo o sistema. */}
      {toggleTarget && (
        <ModalConfirmation
          labelTitle={
            toggleTarget.active === false
              ? `Reativar a máquina "${toggleTarget.thing || '—'}"? Ela voltará a aparecer em todas as listagens.`
              : `Desativar a máquina "${toggleTarget.thing || '—'}"? Ela será ocultada de todo o sistema (variáveis vinculadas seguem junto). Os dados serão preservados e a operação pode ser desfeita.`
          }
          labelConfirmation={
            toggleTarget.active === false ? 'REATIVAR' : 'DESATIVAR'
          }
          onClose={togglingActive ? () => {} : confirmarToggleAtivo}
          onCloseConfirmation={() => {
            if (!togglingActive) setToggleTarget(null);
          }}
        />
      )}

      {/* Fechar com mudancas pendentes */}
      {closeConfirmation && (
        <ModalClose
          onClose={() => {
            setIsModalVisible(false);
            setIsModalEditVisible(false);
            setTelaConcluido(false);
            setCloseConfirmation(false);
          }}
          onCloseConfirmation={setCloseConfirmation}
        />
      )}
    </s.Container>
  );
};

export default RelacaoMaquinas;
